import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {
  Sofa, Monitor, Heart, BookOpen, Building2, Smartphone, Globe, Layers,
  Palette, Cpu, Cloud, Package, ArrowRight, Sparkles, CheckCircle2,
  Server, Wifi, Zap, Database, PenTool
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { servicesSEO } from '@constants/seo'

const serviceCategories = [
  {
    icon: Sofa,
    title: 'Furniture Solutions',
    color: '#6366f1',
    items: [
      'School, classroom, and laboratory furniture',
      'Office and institutional furniture',
      'Hospital and medical college furniture',
      'Storage systems and compactors',
      'Customized furniture solutions',
    ],
  },
  {
    icon: Monitor,
    title: 'Digital & Technology Solutions',
    color: '#8b5cf6',
    items: [
      'Smart classrooms and digital school programs',
      'Interactive flat panels and displays',
      'Document digitization and scanning services',
      'Custom software development',
      'IT infrastructure setup and management',
      'Surveillance systems (CCTV & security solutions)',
    ],
  },
  {
    icon: Heart,
    title: 'Health & Nutrition Solutions',
    color: '#ec4899',
    items: [
      'Millet-based nutrition bars and snacks',
      'Vitamin syrups and IFA supplements',
      'Protein supplements',
      'Mid-Day Meal Program support',
      'Central kitchen setup solutions',
    ],
  },
  {
    icon: BookOpen,
    title: 'Education & Academic Supplies',
    color: '#f59e0b',
    items: [
      'School stationery and books',
      'Academic kits and learning materials',
      'Digital tablets and educational devices',
      'Toys and skill-development products',
    ],
  },
  {
    icon: Building2,
    title: 'Infrastructure & Facility Solutions',
    color: '#10b981',
    items: [
      'Antibacterial conductive vinyl flooring',
      'Playground setup (indoor/outdoor)',
      'Gym setup (indoor/outdoor)',
      'Institutional infrastructure development',
    ],
  },
  {
    icon: Smartphone,
    title: 'Mobile Application Development',
    color: '#06b6d4',
    items: [
      'Custom Mobile App Development - Tailored mobile apps to meet your specific business needs',
      'Cross-Platform Development - Reach a wider audience with apps that work seamlessly on multiple platforms',
      'Intuitive and user-friendly interfaces for enhanced customer engagement',
    ],
  },
  {
    icon: Globe,
    title: 'Desktop Website Creation',
    color: '#f97316',
    items: [
      'Responsive Web Design - Create visually appealing and responsive websites for seamless user experience across all devices',
      'E-Commerce Solutions - Build robust online stores with secure payment gateways and inventory management',
      'Content Management Systems (CMS) - Easy-to-use platforms for effortless content updates',
    ],
  },
  {
    icon: Layers,
    title: 'Product Lifecycle Management (PLM)',
    color: '#84cc16',
    items: [
      'PLM Assessment - Customer data, product development processes, environment assessment, quality, compliance process, and resource assessment',
      'PLM Implementation - Business and technology specifications, solution design, customizations, configurations, and implementation',
      'PLM Data Migration - Exclusive Data Migration Framework "Rover" for better control over data migration process',
      'PLM Application Management - Professional Application Management Services ensuring operation of your application',
    ],
  },
  {
    icon: Palette,
    title: 'UI/UX Design & Development',
    color: '#a855f7',
    items: [
      'User-Centric Design - Craft intuitive and engaging user interfaces (UI) for your applications and websites',
      'User Experience Optimization - Enhance user satisfaction by creating seamless and enjoyable experiences (UX)',
      'Responsive Design - Ensure your apps and websites are accessible and visually appealing on all devices',
    ],
  },
  {
    icon: Cpu,
    title: 'Ahad Solutions - IoT Services',
    color: '#14b8a6',
    items: [
      'Strategic IoT Consulting',
      'Full-Cycle IoT Development',
      'IoT Analytics',
      'IoT Application Management',
      'IoT Testing Services',
    ],
  },
  {
    icon: Cloud,
    title: 'Cloud Services',
    color: '#3b82f6',
    items: [
      'Cloud Advisory Services',
      'Cloud Enablement Services',
      'Cloud Implementation Services',
      'Cloud Managed Services',
      'Cloud Azure IoT Hub',
    ],
  },
]

const products = [
  { name: 'Project Tracking App', category: 'Software', description: 'Real-time project monitoring and milestone tracking' },
  { name: 'Budget Record System', category: 'Software', description: 'Comprehensive financial management and budgeting' },
  { name: 'Smart Card Application', category: 'Software', description: 'Secure identification and access management system' },
  { name: 'Accounting & Finance Suite', category: 'Software', description: 'Complete financial operations and reporting' },
  { name: 'Construction Management', category: 'Software', description: 'End-to-end project lifecycle for construction' },
  { name: 'HR & Inventory System', category: 'Software', description: 'Integrated HR, payroll, and inventory management' },
  { name: 'Educational Platform', category: 'Software', description: 'Learning management and student information system' },
  { name: 'IT Labs Setup', category: 'Infrastructure', description: 'Modern computer labs with latest technology' },
  { name: 'Math & Robotics Labs', category: 'Infrastructure', description: 'STEM-focused learning infrastructure' },
  { name: 'Digital Library System', category: 'Infrastructure', description: 'E-library with digital catalog and management' },
  { name: 'Smart Classroom Solutions', category: 'Infrastructure', description: 'Interactive displays and digital teaching tools' },
  { name: 'Security & Surveillance', category: 'Infrastructure', description: 'CCTV and access control systems' },
]

const otherServices = [
  { icon: Server, title: 'Infrastructure Management Services', description: 'End-to-end IT infrastructure setup, management, and optimization for enterprise needs.' },
  { icon: PenTool, title: 'IT Consultancy Services', description: 'Expert guidance on technology strategy, digital transformation, and IT roadmap planning.' },
  { icon: Wifi, title: 'Manpower Outsource Services', description: 'Skilled IT professionals deployment for project-based and long-term requirements.' },
  { icon: Database, title: 'Network Design Services', description: 'Comprehensive network architecture, security, and implementation solutions.' },
  { icon: Zap, title: 'Digital Marketing', description: 'Strategic digital presence, SEO, social media, and content marketing services.' },
]

export const ServicesPage = () => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  })

  return (
    <>
      <Helmet>
        <title>Government Services | Digital Transformation, Infrastructure & Technology Solutions</title>
        <meta name="description" content="Complete government services: Furniture Solutions, Digital Technology, Health & Nutrition, Education Supplies, Infrastructure Development, Mobile Apps, Website Development, PLM Services, IoT Consulting, Cloud Services. ISO certified, serving 50+ institutions across Maharashtra." />
        <meta name="keywords" content="government services, digital transformation, infrastructure development, school furniture, smart classroom, health nutrition, education technology, mobile app development, website development, PLM implementation, IoT consulting, cloud services, Maharashtra government, ISO certified" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href="https://www.eonixa.in/services" />

        {/* Open Graph */}
        <meta property="og:title" content="Government Services | Digital Transformation & Infrastructure Solutions" />
        <meta property="og:description" content="10+ service categories: Furniture, Digital Tech, Healthcare, Education, Infrastructure, Mobile Apps, Websites, PLM, IoT, Cloud. Trusted by government & institutions." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.eonixa.in/services" />
        <meta property="og:image" content="https://www.eonixa.in/og-services.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Government Services | Digital Transformation & Infrastructure Solutions" />
        <meta name="twitter:description" content="10+ service categories for government & institutions. ISO certified solutions across Maharashtra." />

        {/* Structured Data - Services */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "Government Technology and Infrastructure Services",
            "provider": {
              "@type": "Organization",
              "name": "Eonixa Dynamics Private Limited",
              "url": "https://www.eonixa.in"
            },
            "areaServed": {
              "@type": "Country",
              "name": "India"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Our Services",
              "itemListElement": servicesSEO.map((service) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": service.name,
                  "description": service.description
                }
              }))
            }
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[50vh] overflow-hidden pt-24">
        <div className="container-enterprise relative z-10 py-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">What We Offer</span>
            </motion.div>
            <h1 className="mb-6 text-4xl font-bold text-white sm:text-5xl">
              Our Services & <span className="gradient-text">Product Portfolio</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-3xl mx-auto leading-relaxed">
              Comprehensive solutions across Government, Education, Healthcare & Infrastructure sectors.
              We Design, Develop, Deploy, Integrate, Maintain, and Transform.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Categories */}
      <section ref={ref} className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Service Categories</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              End-to-end solutions designed for government and institutional excellence
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {serviceCategories.map((category, index) => {
              const Icon = category.icon
              return (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full"
                    style={{
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{
                          background: `${category.color}20`,
                          border: `1px solid ${category.color}40`,
                        }}
                      >
                        <Icon className="w-6 h-6" style={{ color: category.color }} />
                      </div>
                      <h3 className="text-lg font-semibold text-white">{category.title}</h3>
                    </div>
                    <ul className="space-y-2">
                      {category.items.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-400">
                          <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: category.color }} />
                          <span className="text-justify">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(5, 150, 105, 0.15) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.25)',
                boxShadow: '0 0 30px rgba(16, 185, 129, 0.1)',
              }}
            >
              <Zap className="w-4 h-4 text-emerald-400" />
              <span className="text-sm text-slate-300">Extended Services</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Additional Services</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Complementary services to support your complete digital transformation journey
            </p>
          </motion.div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {otherServices.map((service, index) => {
              const Icon = service.icon
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative"
                >
                  <div className="rounded-2xl p-6 h-full text-center transition-all duration-300 hover:translate-y-[-4px]"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.06)',
                      backdropFilter: 'blur(12px)',
                    }}
                  >
                    <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      <Icon className="w-7 h-7 text-emerald-400" />
                    </div>
                    <h3 className="text-base font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed">{service.description}</p>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Our Products */}
      <section className="py-24 relative overflow-hidden">
        <div className="container-enterprise relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.3 }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full mb-6"
              style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '1px solid rgba(99, 102, 241, 0.25)',
                boxShadow: '0 0 30px rgba(99, 102, 241, 0.1)',
              }}
            >
              <Package className="w-4 h-4 text-primary" />
              <span className="text-sm text-slate-300">Our Products</span>
            </motion.div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">Product Portfolio</h2>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Proprietary software solutions and infrastructure products designed for government and institutional needs
            </p>
          </motion.div>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="group relative"
              >
                <div className="rounded-2xl p-5 h-full transition-all duration-300 hover:translate-y-[-2px]"
                  style={{
                    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.05) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    backdropFilter: 'blur(12px)',
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                      style={{
                        background: product.category === 'Software'
                          ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)'
                          : 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.2) 100%)',
                        border: product.category === 'Software'
                          ? '1px solid rgba(99, 102, 241, 0.3)'
                          : '1px solid rgba(16, 185, 129, 0.3)',
                      }}
                    >
                      <Package className="w-5 h-5" style={{ color: product.category === 'Software' ? '#a5b4fc' : '#6ee7b7' }} />
                    </div>
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full"
                      style={{
                        background: product.category === 'Software' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                        color: product.category === 'Software' ? '#a5b4fc' : '#6ee7b7',
                        border: product.category === 'Software'
                          ? '1px solid rgba(99, 102, 241, 0.2)'
                          : '1px solid rgba(16, 185, 129, 0.2)',
                      }}
                    >
                      {product.category}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2 group-hover:text-primary transition-colors">
                    {product.name}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{product.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 relative overflow-hidden">
        <div className="container-enterprise relative z-10 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Start Your Project?</h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Get in touch with our team to discuss your requirements and discover how we can help transform your institution.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-primary"
            style={{
              background: 'white',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Sparkles className="w-5 h-5" />
            <span>Contact Us Today</span>
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </>
  )
}

export default ServicesPage